// TourModule.js - Tour planning and generation module
import React from 'react';
import { Navigation, MapPin, Clock, Users, Loader, Sparkles } from 'lucide-react';
import TourPlanningSection from '../ItineraryCreator/tour/TourPlanningSection';
import { useAccessControl } from '../../contexts/AccessControlContext';
import FeatureGuard from '../FeatureGuard';

const TourModule = ({
  planningData,
  onPlanningChange,
  isTourGenerating,
  onGenerateTourPlan,
  itinerary,
  className = ''
}) => {
  const { getFieldAccess } = useAccessControl();

  const canPlanTour = getFieldAccess('tourPlanning') === 'edit' || getFieldAccess('tourPlanning') === 'full';

  const tourStats = {
    totalStops: itinerary?.points?.length || 0,
    estimatedDuration: planningData?.duration || 'Not set',
    difficulty: planningData?.pace || 'moderate',
    travelMode: planningData?.mobility || 'walking'
  };

  return (
    <FeatureGuard featureName="tourPlanning" requiredAccess="view_only">
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Navigation className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-semibold text-gray-800">Tour Planning</h2>
        </div>

        {/* Tour Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <MapPin className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-purple-600">{tourStats.totalStops}</div>
            <div className="text-sm text-purple-700">Stops</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <Clock className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-blue-600">{tourStats.estimatedDuration}</div>
            <div className="text-sm text-blue-700">Duration</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <Users className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-green-600">{tourStats.difficulty}</div>
            <div className="text-sm text-green-700">Pace</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <Navigation className="w-6 h-6 text-orange-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-orange-600">{tourStats.travelMode}</div>
            <div className="text-sm text-orange-700">Travel Mode</div>
          </div>
        </div>

        {/* Tour Planning Section */}
        <div className="mb-6">
          <h3 className="font-medium text-gray-800 mb-4">Planning Preferences</h3>
          <TourPlanningSection
            planningData={planningData}
            onPlanningChange={onPlanningChange}
            onGenerateTourPlan={onGenerateTourPlan}
            isTourGenerating={isTourGenerating}
            disabled={!canPlanTour}
          />
        </div>

        {/* Generate Tour Button */}
        <div className="mb-6">
          <button
            onClick={onGenerateTourPlan}
            disabled={!canPlanTour || isTourGenerating}
            className={`w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors ${
              canPlanTour && !isTourGenerating
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isTourGenerating ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            {isTourGenerating ? 'Generating Tour Plan...' : 'Generate AI Tour Plan'}
          </button>
        </div>

        {/* Planning Options */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Time Preferences */}
          <div>
            <h4 className="font-medium text-gray-800 mb-3">Time Preferences</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time of Day
                </label>
                <select
                  value={planningData?.timeOfDay || 'morning'}
                  onChange={(e) => onPlanningChange?.('timeOfDay', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={!canPlanTour}
                >
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration
                </label>
                <select
                  value={planningData?.duration || '2-3 hours'}
                  onChange={(e) => onPlanningChange?.('duration', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={!canPlanTour}
                >
                  <option value="1-2 hours">1-2 hours</option>
                  <option value="2-3 hours">2-3 hours</option>
                  <option value="3-4 hours">3-4 hours</option>
                  <option value="4-6 hours">4-6 hours</option>
                  <option value="full day">Full day</option>
                </select>
              </div>
            </div>
          </div>

          {/* Travel Preferences */}
          <div>
            <h4 className="font-medium text-gray-800 mb-3">Travel Preferences</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Travel Mode
                </label>
                <select
                  value={planningData?.mobility || 'walking'}
                  onChange={(e) => onPlanningChange?.('mobility', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={!canPlanTour}
                >
                  <option value="walking">Walking</option>
                  <option value="cycling">Cycling</option>
                  <option value="driving">Driving</option>
                  <option value="public transport">Public Transport</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pace
                </label>
                <select
                  value={planningData?.pace || 'moderate'}
                  onChange={(e) => onPlanningChange?.('pace', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={!canPlanTour}
                >
                  <option value="leisurely">Leisurely</option>
                  <option value="moderate">Moderate</option>
                  <option value="brisk">Brisk</option>
                  <option value="intensive">Intensive</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Current Plan Summary */}
        {planningData && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-gray-800 mb-2">Current Plan Summary</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Duration:</strong> {planningData.duration}</p>
              <p><strong>Time:</strong> {planningData.timeOfDay}</p>
              <p><strong>Travel:</strong> {planningData.mobility}</p>
              <p><strong>Pace:</strong> {planningData.pace}</p>
              <p><strong>Budget:</strong> {planningData.budget || 'Not specified'}</p>
              {planningData.interests?.length > 0 && (
                <p><strong>Interests:</strong> {planningData.interests.join(', ')}</p>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <h4 className="font-medium text-purple-800 mb-2">Tour Planning Tips:</h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>• Set your time preferences and constraints</li>
            <li>• Choose appropriate travel mode for your audience</li>
            <li>• Consider the physical difficulty level</li>
            <li>• AI will optimize stop order and timing</li>
            <li>• Review and adjust the generated plan as needed</li>
          </ul>
        </div>
      </div>
    </FeatureGuard>
  );
};

export default TourModule;